import { ArticleDetachBanner } from 'articleEditor/components/articleDetachBanner';
import { Editor, EditorContainer } from 'articleEditor/components/editor';
import { HeaderControls } from 'articleEditor/components/headerControls';
import { LocalePickerWithTranslation } from 'articleEditor/components/localePicker/localePickerWithTranslation';
import { SavingIndicator } from 'articleEditor/components/savingIndicator';
import { SeoSidebar } from 'articleEditor/components/seoSidebar';
import { Settings } from 'articleEditor/components/settings';
import { Title } from 'articleEditor/components/title';
import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { ArticleSavingContext } from 'articleEditor/contexts/savingContext';
import { translatingStatusAtom } from 'articleEditor/hooks/translations/useBaseTranslation';
import useArticleEditorBackLink from 'articleEditor/hooks/useArticleEditorBackLink';
import { useAtomValue } from 'jotai';
import { TranslatingType } from 'lib-knowledge-remote-types/general.types';
import { useRegisterEventsCallback } from 'lib-knowledge-utils/analytics';
import Page from 'lib-knowledge-utils/components/page/index';
import { TranslationLoading } from 'lib-knowledge-utils/components/translationLoading';
import { featureFlagsAtom } from 'lib-knowledge-utils/stores/featureFlags';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { AiAssistedBadge } from 'components/aiAssistedBadge';
import { useShowAiProvenance } from 'hooks/useShowAiProvenance';

import s from './articleEditorLayout.module.scss';
import { TranslationDataContext } from './contexts/translationProvider';
import { useImageFileDrop } from './hooks/imageUpload/useImageFileDrop';

export const ArticlePageLayout = (): JSX.Element => {
  const { savingStore } = useContext(ArticleSavingContext);
  const {
    currentArticleDetails: { title },
  } = useSnapshot(savingStore);
  const { text, ...backProps } = useArticleEditorBackLink();
  const articleData = useContext(ArticleDataContext);
  const showAiProvenance = useShowAiProvenance();
  const translatingStatus = useAtomValue(translatingStatusAtom);
  const ref = useRef<HTMLDivElement>(null);
  const imageFileDrop = useImageFileDrop();
  const { featureFlags } = useAtomValue(featureFlagsAtom);
  const [seoSidebarOpen, setSeoSidebarOpen] = useState(false);
  const { draft } = useContext(TranslationDataContext);

  const registerEventsCallback = useRegisterEventsCallback();
  useEffect(() => {
    registerEventsCallback({
      'article:block-translated': ({
        translatingStatus: { type: translatingType },
      }) => {
        /**
         * When translating the whole article, we want to scroll to the bottom
         * every time a block is being saved
         */
        if (translatingType === TranslatingType.ARTICLE) {
          ref.current?.scrollIntoView({ behavior: 'instant' });
        }
      },
    });
  }, [registerEventsCallback]);

  return (
    <Page>
      <Page.Head
        slotLeft={<Page.HeadBackLink backText={text} backProps={backProps} />}
        slotCenter={
          <>
            <Page.HeadTitle title={title} />
            {showAiProvenance && articleData?.aiAssisted && <AiAssistedBadge />}
          </>
        }
        slotRight={
          <HeaderControls
            seoSidebarOpen={seoSidebarOpen}
            onSeoSidebarToggle={() => setSeoSidebarOpen((isOpen) => !isOpen)}
          />
        }
      >
        <SavingIndicator />
      </Page.Head>

      <Page.Tabs>
        <LocalePickerWithTranslation />
      </Page.Tabs>

      <Page.Body>
        <div className={s.editorColumn}>
          <EditorContainer
            onDragOver={imageFileDrop.onDragOver}
            onDragLeave={imageFileDrop.onDragLeave}
            onDrop={imageFileDrop.onDrop}
          >
            <ArticleDetachBanner />
            <Title />
            <Settings />
            <Editor
              onDrop={imageFileDrop.onDrop}
              onPaste={imageFileDrop.onPaste}
            />
            {translatingStatus.type === TranslatingType.ARTICLE && (
              <TranslationLoading className={s.translationLoading} />
            )}
          </EditorContainer>
          {/* This is used to be able to scroll to the bottom while we are translating */}
          <div ref={ref} />
        </div>
        {featureFlags.seoMeta && (
          <SeoSidebar key={draft.languageId} hidden={!seoSidebarOpen} />
        )}
      </Page.Body>
    </Page>
  );
};
